import { Reference, useMutation } from "@apollo/client";
import Download from "@mui/icons-material/Download";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { BigTooltip } from "components/BigTooltip";
import { ConfirmDialog, ResourceInfo } from "components/ConfirmDialog";
import { FloatingSaveBar } from "components/FloatingSaveBar";
import { GeneratePdfButton } from "components/GeneratePdfButton";
import { getFullName } from "encamp-shared/src/utils/name";
import { gql } from "generated-graphql";
import {
  AssignmentType,
  GetReportDetailsQuery,
  Permission,
  TierIiReportKind,
  TierIiReportOrgStatus,
  TierIiReportStep,
} from "generated-graphql/graphql";
import { useReviewerRemove } from "hooks/useAssignees";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFacilityReportEditable } from "hooks/useFacilityReportEditable";
import { useValidatingForm } from "hooks/useValidatingForm";
import pluralize from "pluralize";
import { CurrentUser } from "providers/user";
import { useCallback, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { AssigneesEditorDialog, AssignmentAction } from "../../Reports/AssigneesEditor";
import { useNavigateToNextStep } from "../useNavigateToNextStep";
import { Report, useReport, useReviewIssues } from "../useReport";
import { useTierIIReportReviewStepInputValidation } from "../validationHooks/useTierIIReportReviewStepInputValidation";
import { NotesToRegulatorsForm } from "./NotesToRegulatorsForm";
import { ReasonForRevisionForm } from "./ReasonForRevisionForm";
import { ReviewersList } from "./ReviewersList";
import { SubmitReviewButtons } from "./SubmitReviewButtons";

export type Reviewer = NonNullable<
  NonNullable<GetReportDetailsQuery["tierIIReport"]>["reviewers"]
>[0];

export const SET_IN_REVIEW_STATUS = gql(`
  mutation SetInReviewStatus($reportId: ID!, $inReview: Boolean!) {
    setInReviewStatus(id: $reportId, inReview: $inReview) {
      id
      organizationStatus
      reviewers {
        id
        isComplete
      }
    }
  }
`);

export const SEND_REVIEWER_EMAIL = gql(`
  mutation EmailReviewers($reportId: ID!) {
    emailReviewers(reportId: $reportId) {
      success
      ids
    }
  }`);

export const SEND_SPECIFIC_REVIEWER_EMAIL = gql(`
  mutation EmailSpecificReviewer($reportId: ID!, $personId: ID!) {
    emailSpecificReviewer(reportId: $reportId, personId: $personId) {
      success
      ids
    }
  }`);

const COMPLETE_REVIEW = gql(`
  mutation CompleteReview($reportId: ID!) {
    completeTierIIReportReviewStep(reportId: $reportId) {
      id
      reviewCompletedByUserId
    }
  }
`);

const UPDATE_REQUIRES_REVIEWERS = gql(`
  mutation UpdateRequiresReviewers($reportId: ID!, $facilityId: ID!, $requiresReviewers: Boolean!) {
    updateRequiresReviewers(id: $reportId, facilityId: $facilityId, requiresReviewers: $requiresReviewers) {
      id
      requiresReviewers
      organizationStatus
    }
  }
`);

type FormState = {
  reasonForReport: string;
  isReportInReview: boolean;
};

export function ReviewForm() {
  const navigate = useNavigate();
  const theme = useTheme();
  const alerts = useAlerts();
  const { reportId = "" } = useParams<{ reportId: string }>();
  const { tenantId = "" } = useParams<{ tenantId: string }>();
  const { data } = useReport();
  const { user } = useCurrentUser();
  const { hasPermissionForFacility } = useAuthorization();
  const navigateToNextStep = useNavigateToNextStep({
    currentStep: TierIiReportStep.Review,
  });
  const [remindersSent, setRemindersSent] = useState<string[]>([]);
  const [addReviewerOpen, setAddReviewerOpen] = useState(false);
  const [showReadyForReview, setShowReadyForReview] = useState(false);
  const [showNotReadyForReview, setShowNotReadyForReview] = useState(false);
  const [showNoAdditionalReviewersNeeded, setShowNoAdditionalReviewersNeeded] = useState(false);
  // Use requiresReviewers boolean directly from the database (defaults to true if undefined)
  const requiresReviewers = data?.tierIIReport?.requiresReviewers ?? true;

  const [localRequiresReviewers, setLocalRequiresReviewers] = useState(requiresReviewers);

  const [showNotAllReviewsComplete, setShowNotAllReviewsComplete] = useState(false);

  const { canAddAssigneesOrReviewers } = useFacilityReportEditable();

  const canVerifyAndSubmit = hasPermissionForFacility(data?.tierIIReport?.facility.id ?? "", [
    Permission.VerifyTierIiReport,
  ]);

  const [setInReviewStatus, { loading: settingReviewStatus }] = useMutation(SET_IN_REVIEW_STATUS, {
    update(cache, { data }) {
      const reportData = data?.setInReviewStatus;
      if (!reportData) {
        return;
      }
      cache.modify({
        id: cache.identify({
          __typename: "TierIIReport",
          id: reportData.id,
        }),
        fields: {
          organizationStatus() {
            return reportData.organizationStatus;
          },
          reviewers: (existingRefs: readonly Reference[] = []) => {
            return existingRefs.map((ref: Reference) => {
              const updatedReviewer = reportData.reviewers.find((r: any) => r.id === ref.__ref);
              if (updatedReviewer) {
                return {
                  ...ref,
                  isComplete: updatedReviewer.isComplete,
                };
              }
              return ref;
            });
          },
        },
      });
    },
  });

  const [removeTierIIReportReviewers, { loading: removingReviewer }] = useReviewerRemove();

  const [updateRequiresReviewers] = useMutation(UPDATE_REQUIRES_REVIEWERS, {
    update: (cache, { data }) => {
      if (data?.updateRequiresReviewers) {
        cache.modify({
          id: cache.identify({
            __typename: "TierIIReport",
            id: reportId,
          }),
          fields: {
            requiresReviewers() {
              return data.updateRequiresReviewers.requiresReviewers;
            },
          },
          optimistic: true,
          broadcast: true,
        });
      }
    },
  });

  const handleRemoveReviewers = useCallback(
    async (personIds: string[]) => {
      try {
        if (!reportId || !personIds.length) {
          return;
        }

        setLocalRequiresReviewers(false);

        await removeTierIIReportReviewers({
          variables: { tierIIReportIds: [reportId], personIds },
        });

        // Update the reviewer requirement setting to no-review
        await updateRequiresReviewers({
          variables: {
            reportId,
            facilityId: data?.tierIIReport?.facility.id ?? "",
            requiresReviewers: false,
          },
        });
        alerts.success(
          `Successfully removed ${personIds.length} ${pluralize(
            "reviewer",
            personIds.length
          )} and set report to not require additional reviewers`
        );
      } catch (error) {
        alerts.error("There was a problem removing the reviewers");
      }
      setShowNoAdditionalReviewersNeeded(false);
    },
    [
      reportId,
      removeTierIIReportReviewers,
      updateRequiresReviewers,
      alerts,
      data?.tierIIReport?.facility.id,
    ]
  );

  const [completeReview, { loading: completingReview }] = useMutation(COMPLETE_REVIEW, {
    update: (cache) => {
      if (user?.id) {
        cache.modify({
          id: cache.identify({
            __typename: "TierIIReport",
            id: reportId,
          }),
          fields: {
            reviewCompletedByUserId() {
              return user?.id;
            },
          },
          optimistic: true,
          broadcast: true,
        });
      }
    },
  });

  const completeReviewAndNavigate = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      await completeReview({ variables: { reportId } });
      navigateToNextStep(true, TierIiReportStep.Review);
    } catch (error) {
      alerts.error("Unable to complete the review step", error);
    }
  }, [user, completeReview, reportId, navigateToNextStep, alerts]);

  const [sendSpecificReviewerEmail, { loading: sendingSpecificEmail }] = useMutation(
    SEND_SPECIFIC_REVIEWER_EMAIL,
    {
      onCompleted: (data) => {
        if (data.emailSpecificReviewer.success) {
          if (data.emailSpecificReviewer.ids.length > 0) {
            setRemindersSent((prev) => [...(prev ?? []), ...data.emailSpecificReviewer.ids]);
            alerts.success("Email sent to reviewer");
          } else {
            alerts.error("No valid reviewer found to email");
          }
        } else {
          alerts.error("An error occurred while sending an email to the reviewer");
        }
      },
      onError: (error) => {
        alerts.error("An error occurred while sending an email to the reviewer", error);
      },
    }
  );

  const state = useMemo(
    () => calculateReviewState(data?.tierIIReport, user),
    [data?.tierIIReport, user]
  );

  const formValues: FormState = useMemo(
    () => ({
      isReportInReview: state.isReportInReview,
      reasonForReport: data?.tierIIReport?.reasonForReport ?? "",
    }),
    [state.isReportInReview, data?.tierIIReport?.reasonForReport]
  );

  const validate = useTierIIReportReviewStepInputValidation();

  const form = useValidatingForm<FormState>(
    formValues,
    useReviewIssues(),
    async (input) => await validate(input.reasonForReport)
  );

  const readyForReviewClick = useCallback(() => {
    if (!state.isReportInReview) {
      setShowReadyForReview(true);
    } else {
      setShowNotReadyForReview(true);
    }
    return false;
  }, [state.isReportInReview]);

  const handleSetInReviewStatus = useCallback(
    async (inReview: boolean) => {
      try {
        await setInReviewStatus({
          variables: {
            reportId,
            inReview,
          },
        });

        alerts.success(`Changed report status to ${inReview ? "In Review" : "In Progress"}`);
      } catch (error) {
        alerts.error("There was a problem updating the review status:", error);
      }
      setShowReadyForReview(false);
      setShowNotReadyForReview(false);
    },
    [alerts, reportId, setInReviewStatus]
  );

  const handleSendSpecificReviewerEmail = (personId: string) => {
    sendSpecificReviewerEmail({ variables: { reportId, personId } });
  };

  const handleRequiresReviewersChange = async (newRequiresReviewers: boolean) => {
    if (localRequiresReviewers === true && newRequiresReviewers === false && reviewers.length > 0) {
      // Show confirmation dialog when switching from requiring reviewers to not requiring them when there are reviewers to remove
      setShowNoAdditionalReviewersNeeded(true);
    } else {
      // Update local state immediately for better UX
      setLocalRequiresReviewers(newRequiresReviewers);

      // Persist the choice to the database
      try {
        await updateRequiresReviewers({
          variables: {
            reportId,
            facilityId: data?.tierIIReport?.facility.id ?? "",
            requiresReviewers: newRequiresReviewers,
          },
        });
      } catch (error) {
        // Revert local state on error
        setLocalRequiresReviewers(localRequiresReviewers);
        alerts.error("Failed to update reviewer requirements", error);
      }
    }
  };
  const canEditReviewers = data?.tierIIReport
    ? canAddAssigneesOrReviewers({
        facilityId: data.tierIIReport.facility?.id ?? "",
        organizationStatus: data.tierIIReport.organizationStatus,
        reportingYear: data.tierIIReport.reportingYear,
      })
    : false;

  const canManageReviewerRequirements = hasPermissionForFacility(
    data?.tierIIReport?.facility.id ?? "",
    [Permission.ManageReviewerRequirements]
  );

  // Reason for revision is currently required for all Revisions
  const showReasonForReport = data?.tierIIReport.reportKind === TierIiReportKind.Revision;

  const reviewers = data?.tierIIReport?.reviewers ?? [];

  const completeReviewStepButtonTooltip = useMemo(() => {
    if (!canVerifyAndSubmit) {
      return (
        <Typography variant="caption">
          Your user does not have permission to complete this step. Please reach out to Encamp if
          you have questions.
        </Typography>
      );
    }

    if (localRequiresReviewers && !state.reportHasReviewers) {
      return (
        <Typography variant="caption">
          The review step cannot be completed because no reviewers are marked as required
        </Typography>
      );
    }

    if (localRequiresReviewers && !state.allReviewsComplete) {
      return (
        <>
          <Typography variant="caption" sx={{ marginBottom: theme.spacing(1) }}>
            The review step cannot be completed because there are outstanding reviews.
          </Typography>
          <Typography variant="caption">
            If these reviews will not be completed, please remove the reviewers from the report and
            indicate that additional reviewers are not required.
          </Typography>
        </>
      );
    }
    return undefined;
  }, [
    canVerifyAndSubmit,
    localRequiresReviewers,
    state.reportHasReviewers,
    state.allReviewsComplete,
    theme,
  ]);

  const disableCompleteReviewButton = useMemo(() => {
    if (
      (localRequiresReviewers &&
        (!state.reportHasReviewers || !state.allReviewsComplete || !state.isReportInReview)) ||
      !canVerifyAndSubmit
    ) {
      return true;
    }
    return false;
  }, [
    localRequiresReviewers,
    state.reportHasReviewers,
    state.allReviewsComplete,
    state.isReportInReview,
    canVerifyAndSubmit,
  ]);

  return (
    <FormProvider {...form}>
      {showReasonForReport && <ReasonForRevisionForm />}
      <NotesToRegulatorsForm />

      <Stack spacing={theme.spacing(3)}>
        <Stack spacing={theme.spacing(1)}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: theme.spacing(1) }}
            spacing={2}
          >
            <Typography variant="h5" sx={{ flexShrink: 0 }}>
              Reviewers
            </Typography>
            {localRequiresReviewers && canManageReviewerRequirements && canVerifyAndSubmit && (
              <IncludeReviewersRadio
                requiresReviewers={localRequiresReviewers}
                handleRequiresReviewersChange={handleRequiresReviewersChange}
                facilityId={data?.tierIIReport?.facility.id ?? ""}
                canManageReviewerRequirements={canManageReviewerRequirements}
              />
            )}
          </Stack>
          <Stack direction="row">
            {localRequiresReviewers && (
              <FormControlLabel
                control={<Checkbox checked={!!state.isReportInReview} />}
                label="This report is ready for review"
                onClick={readyForReviewClick}
              />
            )}
          </Stack>
          {localRequiresReviewers ? (
            <ReviewersList
              report={data?.tierIIReport}
              canDelete={canEditReviewers}
              setAddReviewerOpen={setAddReviewerOpen}
              onSendEmail={handleSendSpecificReviewerEmail}
              canSendEmail={state.isReportInReview}
              isEmailLoading={sendingSpecificEmail}
              alreadySentRemindersList={remindersSent}
              currentUserIsReviewer={state.currentUserReviewer}
              canVerifyAndSubmit={canVerifyAndSubmit}
            />
          ) : (
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={2}
              gap={theme.spacing(10)}
            >
              <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                If your organization requires other individuals to review this report, you can add
                them here. They'll be emailed when you mark the report as ready for review.
              </Typography>
              {canVerifyAndSubmit && (
                <IncludeReviewersRadio
                  requiresReviewers={localRequiresReviewers}
                  handleRequiresReviewersChange={handleRequiresReviewersChange}
                  facilityId={data?.tierIIReport?.facility.id ?? ""}
                  canManageReviewerRequirements={canManageReviewerRequirements}
                />
              )}
            </Stack>
          )}
        </Stack>
        {state.isReportInReview && state.currentUserReviewer && (
          <SubmitReviewButtons reviewer={state.currentUserReviewer} />
        )}
      </Stack>
      <FloatingSaveBar
        saveText={"Complete Review Step"}
        saveDisabled={disableCompleteReviewButton}
        onSaveClick={async () => {
          if (state.allReviewsComplete) {
            await completeReviewAndNavigate();
          } else {
            setShowNotAllReviewsComplete(true);
          }
        }}
        saving={settingReviewStatus || completingReview}
        hideCancel={true}
        sx={{
          marginTop: theme.spacing(1.5),
          marginBottom: theme.spacing(-3),
        }}
        leftChildren={
          <Grid>
            <GeneratePdfButton
              startIcon={<Download />}
              fullWidth
              variant="text"
              size="medium"
              reportId={data?.tierIIReport.id ?? ""}
            />
          </Grid>
        }
        saveTooltipTitle={completeReviewStepButtonTooltip}
      />

      {addReviewerOpen && (
        <AssigneesEditorDialog
          assignmentType={AssignmentType.Reviewer}
          assignmentAction={AssignmentAction.Assign}
          onClose={() => setAddReviewerOpen(false)}
          refetchQueries={[]}
          clearAssigneesOnClose={false}
          tierIIReports={data?.tierIIReport ? [data?.tierIIReport] : []}
          tenantId={tenantId}
        />
      )}
      <ConfirmDialog
        open={showReadyForReview}
        onClose={() => setShowReadyForReview(false)}
        header="Are you sure you want to mark this report as ready for review?"
        loading={settingReviewStatus}
        confirmText="Yes, Mark as Ready"
        onConfirm={() => handleSetInReviewStatus(true)}
        info={
          <>
            <Typography>
              The status of this report will be changed to "In Review" and all reviewers for this
              report will receive an email notification to begin their review.
            </Typography>
            <br />
            <Typography>Are you sure you want to continue?</Typography>
          </>
        }
      />
      <ConfirmDialog
        open={showNotReadyForReview}
        onClose={() => setShowNotReadyForReview(false)}
        header="Are you sure you want to mark this report as not ready for review?"
        confirmText="Yes, Mark as Not Ready"
        loading={settingReviewStatus}
        onConfirm={() => handleSetInReviewStatus(false)}
        info={
          <>
            <Typography sx={{ marginBottom: theme.spacing(1) }}>
              The status of this report will be changed to "In Progress" and any completed reviews
              will need to be re-reviewed if this report is marked "ready for review" again in the
              future.
            </Typography>
            <Typography>Are you sure you want to continue?</Typography>
          </>
        }
      />
      <ConfirmDialog
        open={showNotAllReviewsComplete}
        onClose={() => setShowNotAllReviewsComplete(false)}
        header="Are you sure you want to complete the review for this report?"
        confirmText="Yes, Complete Review"
        loading={completingReview}
        onConfirm={completeReviewAndNavigate}
        info="Not all reviewers have completed their review of this report."
      />
      {showNoAdditionalReviewersNeeded && (
        <ConfirmDialog
          open={showNoAdditionalReviewersNeeded}
          onClose={() => setShowNoAdditionalReviewersNeeded(false)}
          header="Are you sure this report does not require additional reviewers?"
          confirmText="Yes, do not require review"
          onConfirm={() => {
            handleRemoveReviewers(reviewers.map((reviewer) => reviewer.person.id));
          }}
          resources={getReviewersResourceInfo(reviewers)}
          preResourcesInfo={
            <Typography sx={{ marginBottom: theme.spacing(2) }}>
              This action will remove the {reviewers.length}{" "}
              {pluralize("reviewer", reviewers.length)} already assigned to this report.
            </Typography>
          }
          loading={removingReviewer}
        />
      )}
    </FormProvider>
  );
}

function getReviewersResourceInfo(reviewers: Reviewer[]): ResourceInfo[] {
  const maxReviewers = reviewers.slice(0, 3).map((reviewer) => ({
    resourceType: "Reviewer Name",
    resourceName: getFullName(reviewer.person),
  }));

  if (reviewers.length > 3) {
    maxReviewers.push({
      resourceType: `(${reviewers.length - 3} more)`,
      resourceName: "",
    });
  }
  return maxReviewers;
}

type ReviewState = {
  isReportInReview: boolean;
  reportHasReviewers: boolean;
  allReviewsComplete: boolean;
  currentUserReviewer: Reviewer | undefined;
  currentUserHasCompletedReview: boolean;
  sendEmailHidden: boolean;
};

/**
 * Determines the review state of a given report based on its current status and the user's role.
 *
 * This function evaluates multiple scenarios:
 * - Whether the report is currently under review.
 * - If the current user is one of the reviewers.
 * - The completion status of all reviewers.
 *
 * Based on these conditions, it returns an object detailing:
 * - The appropriate text to display on a save button (`saveText`).
 * - Whether the save action should be enabled (`saveEnabled`).
 * - If the email sending option should be hidden (`sendEmailHidden`).
 * - The type of save action to be performed (`saveType`).
 *
 * @param {Report} report - The report being evaluated.
 * @param {CurrentUser} currentUser - The currently logged-in user.
 * @returns {Object} An object with properties detailing the review state and associated UI elements.
 */
export function calculateReviewState(report?: Report, currentUser?: CurrentUser): ReviewState {
  const status = report?.organizationStatus;
  const reviewers = report?.reviewers ?? [];
  const isReportInReview = status === TierIiReportOrgStatus.InReview;
  const reportHasReviewers = reviewers.length > 0;
  const allReviewsComplete = reviewers.every((reviewer) => reviewer.isComplete);

  const state: ReviewState = {
    isReportInReview: report?.organizationStatus === TierIiReportOrgStatus.InReview,
    reportHasReviewers,
    allReviewsComplete,
    currentUserReviewer: undefined,
    currentUserHasCompletedReview: false,
    sendEmailHidden: true,
  };

  if (!report || !currentUser) {
    return state;
  }

  const currentUserReviewer = reviewers.find(
    (reviewer) => reviewer.person.id === currentUser?.person?.id
  );
  const currentUserHasCompletedReview = currentUserReviewer?.isComplete;
  state.currentUserReviewer = currentUserReviewer;
  state.currentUserHasCompletedReview = !!currentUserHasCompletedReview;

  if (isReportInReview && reportHasReviewers && !allReviewsComplete) {
    state.sendEmailHidden = false;
  }
  return state;
}

function IncludeReviewersRadio({
  requiresReviewers,
  handleRequiresReviewersChange,
  facilityId,
  canManageReviewerRequirements,
}: {
  requiresReviewers: boolean;
  handleRequiresReviewersChange: (value: boolean) => void;
  facilityId: string;
  canManageReviewerRequirements: boolean;
}) {
  const theme = useTheme();

  return (
    <FormControl disabled={!canManageReviewerRequirements}>
      <RadioGroup
        row
        value={requiresReviewers.toString()}
        onChange={(e) => handleRequiresReviewersChange(e.target.value === "true")}
        sx={{
          flexWrap: "wrap",
          gap: theme.spacing(1),
          minWidth: 0,
        }}
      >
        <FormControlLabel
          value="true"
          control={<Radio size="small" />}
          label="Include reviewers"
          sx={{ minWidth: 0, flexShrink: 0 }}
        />
        <FormControlLabel
          value="false"
          control={<Radio size="small" />}
          label="No review required"
          sx={{ minWidth: 0, flexShrink: 0 }}
        />
        <BigTooltip
          title={
            <Stack spacing={theme.spacing(1)}>
              <Typography sx={{ fontWeight: "bold" }} variant="caption">
                No review required
              </Typography>
              <Typography variant="caption">
                Some organizations use reviewers to ensure there are additional eyes on a report
                before it is submitted. If your organization does not have this sort of process, you
                do not need to add reviewers.
              </Typography>
            </Stack>
          }
        >
          <IconButton size="small" sx={{ flexShrink: 0 }}>
            <InfoOutlined color="disabled" />
          </IconButton>
        </BigTooltip>
      </RadioGroup>
    </FormControl>
  );
}
